/**
 * An inventory item's photo, or nothing at all.
 *
 * Two things make "nothing at all" the important case:
 *
 * - Most items have no photo. An empty grey square in every other row is a column of
 *   nothing pretending to be information.
 * - A URL that has a photo behind it can still fail. The URL is presigned and expires;
 *   locally the object store is in memory and forgets everything on restart. A broken
 *   image icon with alt text next to it looks like a bug in the page, when the honest
 *   answer is that there is nothing to show.
 *
 * So the same component covers both, and both collapse to null rather than to a
 * placeholder. Shared between the list and the detail page for exactly that reason —
 * the detail page had its own bare `<img>` and showed a broken frame on the first item
 * whose bytes had gone.
 */

import { useEffect, useState } from 'react';

export function InventoryPhoto({
  url,
  alt,
  className,
  size,
  frame,
}: {
  url: string | null;
  alt: string;
  /** For the big one. Omit and pass `size` for a square thumbnail. */
  className?: string;
  size?: number;
  /**
   * Classes for a wrapper that goes away with the image.
   *
   * The detail page frames the photo in a card, and a card left behind by a photo that
   * did not load is an empty box plus the gap its neighbour reserves for it. The frame
   * belongs to the image, so it disappears with it.
   */
  frame?: string;
}) {
  const [failed, setFailed] = useState(false);

  // A fresh URL deserves a fresh attempt: the old one may simply have expired.
  useEffect(() => setFailed(false), [url]);

  if (!url || failed) return null;

  const image = (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={className ?? 'rounded border border-border object-cover bg-surface-alt'}
      style={size ? { width: size, height: size } : undefined}
    />
  );

  return frame ? <div className={frame}>{image}</div> : image;
}
